import type { FieldMapping, SchemaSetSnapshot } from '../../services/api';
import type { CanvasEdge, CanvasNode } from '../types';
import {
  CANVAS_INTERMEDIATE_COLUMN_X,
  CANVAS_ROW_SPACING,
} from '../layoutConstants';
import { isTrueLiteralMapping } from '../utils/mappingTypeUtils';
import { clamp, edgePaletteColor } from './edgeStyling';
import { createEdge } from './edgeFactory';
import {
  ensureFieldNode,
  ensureTransformationNode,
  computeTransformationSpacing,
  resolveAbsolutePosition,
} from './nodeFactory';
import { buildQualified, parseQualifiedName, normaliseDisplayLabel } from './qualifiedNames';
import { getNormalizedSourceFields, getNormalizedTargetField } from './schemaResolution';
import {
  isTrivialDirectStep,
  isInputlessOperation,
  normaliseInputs,
  splitSourceField,
} from './transformAnalysis';
import type { LayoutContext, NodeRegistry } from './types';

export const buildGraphForMapping = (
  mapping: FieldMapping,
  registry: NodeRegistry,
  nodes: CanvasNode[],
  edges: CanvasEdge[],
  layout: LayoutContext,
  getNextIndex: (groupId: string) => number,
  sourceSnapshot?: SchemaSetSnapshot | null,
  sourceAliasMap?: Record<string, string>,
  targetSnapshot?: SchemaSetSnapshot | null,
  targetAliasMap?: Record<string, string>
) => {
  const sourceAlias = (mapping.source_file ?? 'source').trim() || 'source';
  const targetAlias = (mapping.target_table ?? 'target').trim() || 'target';

  const normalizedTargetField = getNormalizedTargetField(mapping, targetSnapshot, targetAliasMap);

  const targetQualified = buildQualified(mapping.target_table, normalizedTargetField, targetAlias);
  const sourceGroup = layout.sourceGroups.get(sourceAlias);
  const targetGroup = layout.targetGroups.get(targetAlias);

  // Phase 1 (P0): Skip entire mapping graph when BOTH source and target groups are collapsed.
  // When only one side is collapsed, we still skip that side's field nodes (handled below),
  // but we don't create edges either — edges require both endpoints to exist.
  const sourceCollapsed = sourceGroup && !sourceGroup.expanded;
  const targetCollapsed = targetGroup && !targetGroup.expanded;
  if (sourceCollapsed || targetCollapsed) {
    return;
  }

  // -- Source Nodes (Multi-field support) --
  const sourceFields = getNormalizedSourceFields(mapping, sourceSnapshot, sourceAliasMap);

  const hasRealSource = !isTrueLiteralMapping(mapping.transformation_type, mapping.transformation || '') && Boolean(mapping.source_field?.trim()) && Boolean(sourceGroup);

  const sourceNodes: CanvasNode[] = [];

  if (hasRealSource) {
    sourceFields.forEach(field => {
      const qualified = buildQualified(mapping.source_file, field, sourceAlias);
      let index = layout.sourceFieldIndex.get(qualified);
      if (index === undefined && sourceGroup) {
        index = getNextIndex(sourceGroup.id);
      }
      const finalIndex = index ?? 0;

      const node = ensureFieldNode(
        registry,
        nodes,
        qualified,
        'sourceField',
        sourceGroup,
        finalIndex,
        mapping,
        false,
        mapping.confidence
      );
      sourceNodes.push(node);
    });
  }

  const primarySourceIndex = sourceNodes.length > 0 ? Math.floor((sourceNodes.length - 1) / 2) : -1;
  const sourceNode = primarySourceIndex >= 0 ? sourceNodes[primarySourceIndex] : undefined;
  const sourceQualified = sourceNode?.data.qualifiedName ?? '';
  const sourceIndex = sourceQualified ? (layout.sourceFieldIndex.get(sourceQualified) ?? 0) : 0;

  // -- Target Node --
  let targetIndex = layout.targetFieldIndex.get(targetQualified);
  if (targetIndex === undefined && targetGroup) {
    targetIndex = getNextIndex(targetGroup.id);
  }

  const finalSourceIndex = sourceIndex;
  const finalTargetIndex = targetIndex ?? 0;
  const rawSteps = (mapping.transformation_steps ?? []).filter((step) => step);
  const effectiveSteps = rawSteps.filter((step) => !isTrivialDirectStep(mapping, step));
  const mappingAiGenerated = Boolean(rawSteps.some((step) => step?.ai_generated));

  const targetNode = ensureFieldNode(
    registry,
    nodes,
    targetQualified,
    'targetField',
    targetGroup,
    finalTargetIndex,
    mapping,
    mappingAiGenerated,
    mapping.confidence
  );

  const stepCount = effectiveSteps.length;
  const isSingleInputless = stepCount === 1 && isInputlessOperation(effectiveSteps[0]);

  if (sourceNodes.length === 0) {
    return;
  }

  if (!stepCount || isSingleInputless) {
    const edgeColor = edgePaletteColor(mapping);
    const edgeStep = isSingleInputless ? effectiveSteps[0] : undefined;
    const edgeLabel = isSingleInputless
      ? (effectiveSteps[0].expression || effectiveSteps[0].operation || effectiveSteps[0].kind || mapping.transformation_type)
      : mapping.transformation_type;
    sourceNodes.forEach(node => {
      createEdge(edges, node.id, targetNode.id, edgeLabel, mapping, edgeStep, mapping.confidence, mappingAiGenerated, edgeColor, {
        jitterScale: 0.1,
      });
    });
  } else {
    // Transformation Steps Logic
    const transformationSpacing = computeTransformationSpacing(stepCount);
    const producerByQualified = new Map<string, string>();
    const producerByLower = new Map<string, string>();
    const producerByFieldOnly = new Map<string, string>();
    sourceNodes.forEach(node => {
      producerByQualified.set(node.data.qualifiedName, node.id);
      producerByLower.set(node.data.qualifiedName.toLowerCase(), node.id);
      const { field } = parseQualifiedName(node.data.qualifiedName);
      if (field && !producerByFieldOnly.has(field.toLowerCase())) {
        producerByFieldOnly.set(field.toLowerCase(), node.id);
      }
    });
    const stepNodes: CanvasNode[] = [];

    const sourcePos = resolveAbsolutePosition(sourceNode!, nodes);
    const targetPos = resolveAbsolutePosition(targetNode, nodes);
    const sourceCenterY = sourcePos.y + CANVAS_ROW_SPACING / 2;
    const targetCenterY = targetPos.y + CANVAS_ROW_SPACING / 2;
    const transformationApproxHeight = 64;
    const computeStepY = (index: number): number => {
      const ratio = (index + 1) / (stepCount + 1);
      const centerY = sourceCenterY + (targetCenterY - sourceCenterY) * ratio;
      return centerY - transformationApproxHeight / 2;
    };

    effectiveSteps.forEach((step, index) => {
      const stepY = computeStepY(index);
      const stepNode = ensureTransformationNode(nodes, mapping, step, index, stepY, stepCount, transformationSpacing);
      stepNodes.push(stepNode);
      const stepColor = edgePaletteColor(mapping, step);

      const inputs = normaliseInputs(mapping, step);
      if (!inputs.length && index > 0) {
        const previous = stepNodes[index - 1];
        createEdge(edges, previous.id, stepNode.id, step.kind, mapping, step, step.confidence, step.ai_generated, stepColor, {
          offset: clamp((finalTargetIndex - finalSourceIndex) * 18 + (index + 1) * 10, -180, 180),
          jitterScale: 0.32,
        });
      } else if (!inputs.length && index === 0) {
        if (sourceNodes.length > 0) {
          sourceNodes.forEach(sn => {
            createEdge(edges, sn.id, stepNode.id, step.kind, mapping, step, step.confidence, step.ai_generated, stepColor, {
              jitterScale: 0.26,
            });
          });
        }
      } else {
        inputs.forEach((input, inputIndex) => {
          const { qualified, field: inputField } = parseQualifiedName(input, mapping.source_file);
          const producer = producerByQualified.get(qualified)
            ?? producerByLower.get(qualified.toLowerCase())
            ?? producerByFieldOnly.get(inputField.toLowerCase());
          if (producer) {
            createEdge(edges, producer, stepNode.id, step.kind, mapping, step, step.confidence, step.ai_generated, stepColor, {
              offset: clamp((inputIndex - (inputs.length - 1) / 2) * 28, -200, 200),
              jitterScale: 0.26,
            });
            return;
          }
          const inputOffset = (inputIndex - (inputs.length - 1) / 2) * 18;
          const inferredNode = ensureFieldNode(
            registry,
            nodes,
            qualified,
            'intermediateField',
            undefined,
            0,
            undefined,
            step.ai_generated,
            step.confidence,
            { x: CANVAS_INTERMEDIATE_COLUMN_X, y: stepY + inputOffset }
          );
          producerByQualified.set(qualified, inferredNode.id);
          producerByLower.set(qualified.toLowerCase(), inferredNode.id);
          createEdge(edges, inferredNode.id, stepNode.id, step.kind, mapping, step, step.confidence, step.ai_generated, stepColor, {
            offset: clamp((inputIndex - (inputs.length - 1) / 2) * 24, -180, 180),
            jitterScale: 0.26,
          });
        });
      }

      const outputs = step.outputs?.filter(Boolean) ?? [];
      if (outputs.length) {
        outputs.forEach((output) => {
          const { qualified: outQ, field: outField } = parseQualifiedName(output, mapping.target_table);
          producerByQualified.set(outQ, stepNode.id);
          producerByLower.set(outQ.toLowerCase(), stepNode.id);
          if (outField) {
            producerByFieldOnly.set(outField.toLowerCase(), stepNode.id);
          }
        });
      } else {
        producerByQualified.set(`${mapping.mapping_id}::step-${index}`, stepNode.id);
      }
    });

    const lastStep = stepNodes[stepNodes.length - 1];
    const preferredOutput = producerByQualified.get(targetQualified) ?? lastStep.id;
    createEdge(
      edges,
      preferredOutput,
      targetNode.id,
      mapping.transformation_type ?? lastStep.data.step?.kind,
      mapping,
      lastStep.data.step,
      mapping.confidence,
      mappingAiGenerated,
      edgePaletteColor(mapping, lastStep.data.step),
      {
        offset: clamp((finalTargetIndex - finalSourceIndex) * 24 + stepCount * 12, -220, 220),
        jitterScale: 0.38,
      }
    );
  }
};
